import axios from "axios";

const loadingStart = () => window.dispatchEvent(new Event("harwin:http:start"));
const loadingEnd = () => window.dispatchEvent(new Event("harwin:http:end"));

// Prevent accidental double-click submissions across normal JSON CRUD forms.
// File uploads/FormData and callers that explicitly opt out are not deduped.
const recentMutations = new Map();
const MUTATION_DEDUPE_MS = 900;
const mutationSignature = (config) => {
  const method = String(config.method || "get").toLowerCase();
  if (!["post", "put", "patch", "delete"].includes(method)) return null;
  if (config.allowDuplicateRequest === true) return null;
  if (typeof FormData !== "undefined" && config.data instanceof FormData)
    return null;
  let body = "";
  try {
    body =
      typeof config.data === "string"
        ? config.data
        : JSON.stringify(config.data || {});
  } catch (_) {}
  return `${method}|${config.url || ""}|${body}`;
};

// ✅ REQUEST INTERCEPTOR - Adds token to every request
axios.interceptors.request.use(
  (config) => {
    const signature = mutationSignature(config);
    if (signature) {
      const now = Date.now();
      const last = recentMutations.get(signature) || 0;
      if (now - last < MUTATION_DEDUPE_MS) {
        const error = new Error(
          "Please wait — this action is already being submitted.",
        );
        error.code = "DUPLICATE_SUBMIT";
        error.config = { ...config, skipGlobalLoading: true };
        return Promise.reject(error);
      }
      recentMutations.set(signature, now);
      window.setTimeout(() => {
        if (recentMutations.get(signature) === now)
          recentMutations.delete(signature);
      }, MUTATION_DEDUPE_MS + 100);
    }
    if (!config.skipGlobalLoading) {
      config.__harwinGlobalLoading = true;
      loadingStart();
    }
    const token = localStorage.getItem("token");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    loadingEnd();
    return Promise.reject(error);
  },
);

// ✅ RESPONSE INTERCEPTOR - Handles auth errors
axios.interceptors.response.use(
  (response) => {
    if (response.config?.__harwinGlobalLoading) loadingEnd();
    return response;
  },
  (error) => {
    if (error.config?.__harwinGlobalLoading) loadingEnd();
    const status = error.response?.status;
    const requestUrl = error.config?.url || "";

    // ✅ FIX: Skip interceptor logic for login/reset endpoints
    // A 401 on /login means wrong password — NOT an expired token
    const isAuthEndpoint =
      requestUrl.includes("/login") ||
      requestUrl.includes("/reset-password") ||
      requestUrl.includes("/debug-user");

    // A 403 can be returned by background/auto-fetch requests too. Do not show a
    // global permission toast for every 403: page/action handlers own user-facing
    // permission feedback (Edit/Delete/Create/Download etc.) so normal rendering,
    // filtering and refreshes never spam "no permission" messages. A caller that
    // genuinely needs central handling can explicitly opt in.
    if (status === 403 && error.config?.showPermissionToast === true) {
      const message =
        error.response?.data?.message ||
        "You don't have permission to perform this action.";
      window.dispatchEvent(
        new CustomEvent("harwin:access-denied", { detail: { message } }),
      );
    }

    if (status === 401 && !isAuthEndpoint) {
      console.error("❌ 401 Unauthorized - Token invalid/expired");

      const currentPath = window.location.pathname;

      if (!currentPath.includes("/login")) {
        localStorage.clear();
        window.location.href = "/login";
      }
    }

    return Promise.reject(error);
  },
);

export default axios;
